import instantsearch from 'instantsearch.js';
import {
  searchClient,
  setAlgoliaQueryID,
  trackAlgoliaEvent,
} from '~/js/utilities/algolia';
import {
  configure,
  rangeSlider,
  refinementList,
} from 'instantsearch.js/es/widgets';
import {
  connectCurrentRefinements,
  connectClearRefinements,
  connectDynamicWidgets,
  connectQueryRules,
  connectInfiniteHits,
  connectRange,
  connectRefinementList,
  connectSearchBox,
  connectSortBy,
} from 'instantsearch.js/es/connectors';
import {
  algoliaSortSizes,
  getPrintFacets,
  createRouting,
} from '~/js/utilities/algolia';
import { fetchCollections } from '~/js/utilities/products-and-collections';
import { captureSentryMessage } from '~/js/utilities/sentry';
import {
  getCachedInstantSearchResults,
  setCachedInstantSearchResults,
} from '~/js/utilities/algolia/cache';
import { createDebouncer } from '~/js/utilities';
import { trackGoogleAnalyticsEvent } from '~/js/utilities/analytics';

const RESULTS_PER_PAGE = 60;

const CACHE_WRITE_DEBOUNCE_MS = 1000;

const debouncer1000 = createDebouncer((cb) =>
  setTimeout(cb, CACHE_WRITE_DEBOUNCE_MS)
);

const loadSwatches = () => {
  const normalizeSwatch = (swatch) => {
    if (swatch.color_image)
      return {
        image: swatch.color_image?.includes('url(')
          ? swatch.color_image
          : `url(${swatch.color_image})`,
      };
    if (swatch.color_gradient) return { gradient: swatch.color_gradient };
    return { color: swatch.color };
  };

  return Object.fromEntries(
    (window.COLOR_SWATCHES || []).map((swatch) => [
      swatch.heading,
      normalizeSwatch(swatch),
    ])
  );
};

const algoliaBrowser = (
  context,
  collectionHandle,
  facets,
  mediaArray
  // ...otherParams
) => ({
  context,
  collectionHandle,
  isLoadingInitial: true,
  instantSearchClient: null,
  products: [],
  observer: null,
  loading: false,
  facets: facets || [],
  error: '',

  backToTopVisible: false,
  filtersDesktopVisible: true,
  descriptionRevealed: false,
  // hasFacetsRefinements: false,
  hasLoadMore: false,
  mediaArray: mediaArray,

  algoliaFacetsData: {},
  priceAdded: false,

  printsLoaded: false,
  prints: [],

  currentRefinements: [],
  printsCollections: [],
  printsCollectionsLoaded: false,

  totalResults: 0,
  query: '',

  smallCardBanners: [],

  refineClearAll: null,

  sortByItems: [
    { label: 'Featured', value: `${window.ALGOLIA_INDEX_NAME}` },
    {
      label: 'Name: A - Z',
      value: `${window.ALGOLIA_INDEX_NAME}_name_asc`,
    },
    {
      label: 'Name: Z - A',
      value: `${window.ALGOLIA_INDEX_NAME}_name_desc`,
    },
    {
      label: 'Price: Low to High',
      value: `${window.ALGOLIA_INDEX_NAME}_price_asc`,
    },
    {
      label: 'Price: High to Low',
      value: `${window.ALGOLIA_INDEX_NAME}_price_desc`,
    },
    {
      label: 'Availability',
      value: `${window.ALGOLIA_INDEX_NAME}_availability_asc`,
    },
  ],

  get hasNoActiveFacetsAndEmptyResults() {
    return this.products.length === 0 && !this.hasFacetsRefinements;
  },

  get isSearchAndHasNoActiveFacets() {
    return this.context === 'search-results' && !this.hasFacetsRefinements;
  },

  get cards() {
    const products = [...this.products];
    const mediaList = this.mediaArray
      .filter((media) => media.media_size === 'small')
      .filter((media) => media.media_type === 'image')
      .filter((media) => media.desktop_position);
    // .filter((media) => media.desktop_position < products.length - 1);

    const cards = [
      ...mediaList.map((m, i) => ({
        ...m,
        key: m.media_id,
        type: 'media',
        position: m.desktop_position,
      })),
      ...products.map((p, i) => ({
        ...p,
        type: 'product',
        position: i + 1,
      })),
    ].sort((a, b) => a.position - b.position);

    return cards;
  },

  get mediaList() {
    return this.mediaArray.filter((media) => media.media_size !== 'small');
    // Desktop position is the row where the media should be displayed, it should be lower than the products length / number of products per row
    // .filter(
    //   (media) =>
    //     media.desktop_position < Math.ceil(this.products.length / 3)
    // )
  },

  get hasFacetsRefinements() {
    return this.currentRefinements?.length > 0;
  },

  get currentRefinementsCount() {
    return this.currentRefinements?.reduce(
      (acc, filter) => acc + filter.refinements.length,
      0
    );
  },

  get facetsList() {
    const list = Object.keys(this.algoliaFacetsData).filter(
      (facet) => facet !== 'price'
    );

    return list;
  },

  init() {
    if (collectionHandle == 'vip-shop-early') {
      window.localStorage.setItem('isVIP', true);
    }

    this.instantSearchClient = instantsearch({
      searchClient,
      indexName: window.ALGOLIA_INDEX_NAME,
      insights: false,
      routing: createRouting(this.facets),
      future: {
        preserveSharedStateOnUnmount: true,
        persistHierarchicalRootCount: true,
      },
    });

    this.renderHits = this.renderHits.bind(this);
    this.debouncedSetCachedResults = debouncer1000((hits) => {
      try {
        setCachedInstantSearchResults(
          this.collectionHandle,
          // hits
          hits.map((hit) => ({
            id: hit.id,
            handle: hit.handle,
            objectID: hit.objectID,
            image: hit.image || hit.product_image,
            meta: hit.meta || { custom: {} },
            __position: hit.__position,
            __queryID: hit.__queryID,
          }))
        );
      } catch (error) {
        console.log('Failed to set the cached results', error);
      }
    });
    this.renderRefinementList = this.renderRefinementList.bind(this);
    this.transformSizes = this.transformSizes.bind(this);
    this.transformColorSwatches = this.transformColorSwatches.bind(this);
    this.transformPrints = this.transformPrints.bind(this);
    this.transformPrintCollections = this.transformPrintCollections.bind(this);
    this.transformAvailability = this.transformAvailability.bind(this);
    this.renderCurrentRefinement = this.renderCurrentRefinement.bind(this);
    const customCurrentRefinements = connectCurrentRefinements(
      this.renderCurrentRefinement
    );
    const instantSearchHits = connectInfiniteHits(this.renderHits);
    const instantSearchRefinementList = connectRefinementList(
      this.renderRefinementList
    );
    const instantSearchRangeRefinement = connectRange(
      this.renderRangeInput.bind(this)
    );
    const virtualSearchBox = connectSearchBox(() => null);
    // const virtualCollectionFacet = connectRefinementList(() => null);
    const instantSearchSortBy = connectSortBy(this.renderSortBy.bind(this));
    const instantSearchClearRef = connectClearRefinements(
      this.renderClearRefinements.bind(this)
    );
    const instantSearchQueryRules = connectQueryRules(
      this.renderQueryRules.bind(this)
    );

    const transformations = {
      inventory_available: this.transformAvailability,
      'options.size': this.transformSizes,
      'options.color': this.transformColorSwatches,
      // 'meta.custom.print': transformPrints,
      'meta.custom.size': this.transformSizes,
      'meta.custom.print_collection': this.transformPrintCollections,
    };

    const instantSearchConfig = {
      hitsPerPage: RESULTS_PER_PAGE,
      distinct: true,
      clickAnalytics: false, // disable click events since we are using the custom hits component
      analytics: true,
      filters: this.collectionHandle
        ? `collections:${this.collectionHandle}`
        : '',
    };

    const instantSearchHitsConfig = {
      container: '#browser-hits',
      showPrevious: true,
      transformItems(items) {
        return items.map((item) => ({
          ...item,
          uid: item.id,
          ss_grouped_pid: [],
        }));
      },
    };

    this.instantSearchClient.addWidgets(
      [
        configure(instantSearchConfig),
        instantSearchHits(instantSearchHitsConfig),
        virtualSearchBox({ container: '#searchbox' }),

        customCurrentRefinements({
          container: document.createElement('div'),
        }),

        instantSearchQueryRules({
          container: document.createElement('div'),
        }),

        // virtualCollectionFacet({
        //   container: document.createElement('div'),
        //   attribute: 'collections',
        // }),

        ...this.facets
          .filter((facet) => facet !== 'price')
          .map((facet) => {
            return instantSearchRefinementList({
              container: document.createElement('div'),
              attribute: facet,
              operator: 'or',
              limit: 8,
              showMore: true,
              showMoreLimit: 500,
              transformItems: transformations[facet] || undefined,
            });
          }),

        rangeSlider({
          container: '#refinement-price-slider',
          attribute: 'price',
          step: 2.5,
          pips: false,
          cssClasses: {
            root: 'products-browser-facet-slider__slider-wrapper',
            handle: 'products-browser-facet-slider__handle',
            lowerHandle: 'products-browser-facet-slider__handle--lower',
            upperHandle: 'products-browser-facet-slider__handle--upper',
            // tooltip: 'products-browser-facet-slider__tooltip',
          },
        }),

        instantSearchRangeRefinement({
          container: '#refinement-price-input',
          attribute: 'price',
          step: 2.5,
        }),

        instantSearchSortBy({
          container: '#refinement-sort-by-desktop',
          items: this.sortByItems,
        }),
        instantSearchSortBy({
          container: '#refinement-sort-by-mobile',
          items: this.sortByItems,
        }),

        instantSearchClearRef({
          container: document.createElement('div'),
          excludedAttributes: ['query', 'collections'],
        }),
      ].filter(Boolean)
    );

    if (this.facets?.includes('meta.custom.size')) {
      this.initQuickSizeFilters();
    }

    this.instantSearchClient.start();
    this.instantSearchClient.on('render', this.onRenderSearch.bind(this));

    this.$watch('isLoadingInitial', (isLoadingInitial) => {
      if (!isLoadingInitial) {
        this.scrollToLastPosition();
      }
    });
  },

  initQuickSizeFilters() {
    const filtersContainer = document.querySelector(
      '#collections-size-filter .filters-container'
    );
    if (!filtersContainer) return;
    // Do not init the filters on desktop
    if (window.innerWidth > 991) {
      filtersContainer.innerHTML = '';
      return;
    }

    this.instantSearchClient.addWidgets([
      refinementList({
        container: filtersContainer,
        attribute: 'meta.custom.size',
        showMore: false,
        limit: 100,
        transformItems: (props) => {
          return this.transformSizes(props);
        },
        sortBy: ['count:desc', 'name:asc'],
        templates: {
          item(item, { html }) {
            const { label, isRefined } = item;

            return html`
              <button
                class="size-filter-label ${isRefined ? 'selected' : ''}"
                data-size="${label}"
              >
                ${label}
              </button>
            `;
          },
        },
      }),
    ]);
  },

  renderCurrentRefinement(options, isFirstRender) {
    const { items } = options;
    const newRefinements = (items || []).filter(
      (item) => item.attribute !== 'collections'
    );

    if (!isFirstRender) {
      const previousFlat = this.currentRefinements.flatMap((r) =>
        r.refinements.map((v) => `${r.attribute}:${v.value}`)
      );
      const newFlat = newRefinements.flatMap((r) =>
        r.refinements.map((v) => `${r.attribute}:${v.value}`)
      );

      const added = newFlat.filter((f) => !previousFlat.includes(f));
      const removed = previousFlat.filter((f) => !newFlat.includes(f));

      added.forEach((f) => {
        const colonIndex = f.indexOf(':');
        trackGoogleAnalyticsEvent('filter_applied', {
          filter_attribute: f.slice(0, colonIndex),
          filter_value: f.slice(colonIndex + 1),
          active_filter_count: newFlat.length,
          search_source: 'instantsearch',
          search_collection: this.collectionHandle || '',
        });
      });
      removed.forEach((f) => {
        const colonIndex = f.indexOf(':');
        trackGoogleAnalyticsEvent('filter_removed', {
          event: 'filter_removed',
          filter_attribute: f.slice(0, colonIndex),
          filter_value: f.slice(colonIndex + 1),
          active_filter_count: newFlat.length,
          search_source: 'instantsearch',
          search_collection: this.collectionHandle || '',
        });
      });
    }

    this.currentRefinements = newRefinements;
  },

  async renderHits(options, isFirstRender) {
    let previousHits = [];
    const { currentPageHits, hits, isLastPage, results, showMore } = options;

    const showMoreAction = () => {
      showMore();
    };

    if (!isFirstRender && this.isLoadingInitial) {
      this.isLoadingInitial = false;
    }

    if (isFirstRender) {
      const button = this.$refs.loadMoreButton;
      if (button) {
        button.addEventListener('click', () => {
          trackGoogleAnalyticsEvent('load_more_results', {
            search_term: this.query,
            page_number: (results?.page ?? 0) + 2,
            search_source: 'instantsearch',
            search_collection: this.collectionHandle || '',
          });
          showMoreAction();
        });
      }
    }

    if (!results) return;

    this.trackViewSearchResults(results);
    try {
      if (results) {
        const isFirstPage = results?.page === 0;

        if (this.products?.length === 0 && !isFirstPage) {
          const cachedResults =
            getCachedInstantSearchResults(this.collectionHandle) ?? [];

          if (cachedResults.length > 0) {
            previousHits = cachedResults.map((cachedHit) => ({
              ...cachedHit,
              uid: cachedHit.id,
              ss_grouped_pid: [],
            }));
          }
        } else if (!isFirstPage && this.products.length > 0) {
          previousHits = this.products;
        }
      }
    } catch (error) {
      console.log('Failed to get the cached results', error);
    }

    // Remove from previousHits if the currentPageHits has de same id
    this.products = previousHits
      .filter((hit) => !currentPageHits.some((p) => p.id === hit.id))
      .concat(currentPageHits);

    this.totalResults = results?.nbHits ? results?.nbHits : 0;
    this.query = results?.query;
    this.hasLoadMore = isLastPage || results?.nbHits === 0 ? false : true;

    if (results?.renderingContent?.redirect) {
      if (results?.renderingContent?.redirect?.url) {
        try {
          window.location.href = results?.renderingContent?.redirect?.url;
        } catch (error) {
          captureSentryMessage(
            'Error redirecting with algolia redirect url',
            'error',
            {
              url: results?.renderingContent?.redirect?.url,
              error: error.message,
            }
          );
        }
      }
    }

    if (results?.queryID) {
      setAlgoliaQueryID(results?.queryID);
    }

    // Store the products in the cache
    this.debouncedSetCachedResults(this.products);
    this.updateHeroSearchTitle(this.query, this.totalResults);
  },

  async renderRefinementList(options, isFirstRender) {
    const { widgetParams } = options;
    const key = widgetParams.attribute.split('.').pop();
    this.algoliaFacetsData[key.toLowerCase()] = options;
  },

  renderRangeInput(options, isFirstRender) {
    const { start, range, canRefine, refine, widgetParams } = options;
    this.algoliaFacetsData['price'] = options;
    const { min, max } = range;
    const container = document.querySelector(widgetParams.container);
    if (!container) return;

    if (isFirstRender) {
      const form = document.createElement('form');
      form.classList.add('products-browser-facet-slider__inputs');

      form.addEventListener('submit', (event) => {
        event.preventDefault();
        const [minInput, maxInput] = event.target.elements;

        const rawMinInputValue = parseFloat(minInput.value);
        const rawMaxInputValue = parseFloat(maxInput.value);

        trackGoogleAnalyticsEvent('filter_applied', {
          filter_attribute: 'price',
          filter_value: `${rawMinInputValue}-${rawMaxInputValue}`,
          active_filter_count: this.currentRefinementsCount,
          search_source: 'instantsearch',
          search_collection: this.collectionHandle || '',
        });

        refine([
          Number.isFinite(rawMinInputValue) ? rawMinInputValue : undefined,
          Number.isFinite(rawMaxInputValue) ? rawMaxInputValue : undefined,
        ]);
      });

      container.appendChild(form);
    }

    const form = container.querySelector('form');

    if (form) {
      form.innerHTML = '';

      for (let index = 0; index < start.length; index++) {
        let value = start[index];
        if (value < range.min) value = range.min;
        if (value > range.max) value = range.max;
        const wrapper = document.createElement('div');
        wrapper.classList.add('products-browser-facet-slider__input-wrapper');

        const text = document.createElement('span');
        text.textContent = '$';

        const label = document.createElement('label');
        label.classList.add('products-browser-facet-slider__input-label');
        const srLabel = document.createElement('span');
        srLabel.classList.add('visually-hidden');
        srLabel.textContent = index === 0 ? 'low' : 'high';

        const input = document.createElement('input');
        input.type = 'number';
        input.min = min;
        input.max = max;
        input.step = '2.5';
        input.value = value;
        input.classList.add('products-browser-facet-slider__input');

        label.appendChild(srLabel);
        label.appendChild(input);
        wrapper.appendChild(text);
        wrapper.appendChild(label);

        form.appendChild(wrapper);
      }
    }
  },

  renderSortBy(renderOptions, isFirstRender) {
    const { options, currentRefinement, canRefine, refine, widgetParams } =
      renderOptions;
    const container = document.querySelector(widgetParams.container);
    if (!container) return;

    if (isFirstRender) {
      const form = document.createElement('form');
      form.classList.add('products-browser-sort__form');
      form.id = 'aa-refinement-sort-by';
      form.addEventListener('change', (event) => {
        if (event.target.name === 'aa-sort-by') {
          this.$dispatch('aa-browser-sort', event.target.value);
          refine(event.target.value);

          const label = this.sortByItems.find(
            (o) => o.value === event.target.value
          )?.label;
          trackGoogleAnalyticsEvent('sort_changed', {
            sort_value: event.target.value,
            sort_label: label,
            search_source: 'instantsearch',
            search_collection: this.collectionHandle || '',
          });
        }
      });

      container.appendChild(form);
    }

    container.querySelector('form').innerHTML = '';
    const wrapperLabel = document.querySelectorAll(
      '.products-browser-sort__button-label'
    );

    if (wrapperLabel) {
      wrapperLabel.forEach((label) => {
        const textLabel = options.find(
          (opt) => opt.value === currentRefinement
        );
        if (textLabel) {
          label.textContent = textLabel.label;
        }
      });
    }

    options.forEach((option) => {
      const label = document.createElement('label');
      label.classList.add('products-browser-sort__option');

      const input = document.createElement('input');
      input.type = 'radio';
      input.name = 'aa-sort-by';
      input.value = option.value;
      input.checked = option.value === currentRefinement;
      input.classList.add('products-browser-sort__option-radio');

      const span = document.createElement('span');
      span.textContent = option.label;
      span.classList.add('products-browser-sort__option-label');

      label.appendChild(input);
      label.appendChild(span);
      container.querySelector('form').appendChild(label);
    });
  },

  renderClearRefinements(renderOptions, isFirstRender) {
    const { refine } = renderOptions;
    this.refineClearAll = refine;

    if (isFirstRender) {
      const buttons = document.querySelectorAll(
        '.products-browser__top-desktop-clear-all, .products-browser-facets-wrapper__button--clear-all'
      );
      if (buttons) {
        buttons.forEach((button) => {
          button.addEventListener('click', () => {
            trackGoogleAnalyticsEvent('filters_cleared', {
              cleared_filter_count: this.currentRefinementsCount,
              search_source: 'instantsearch',
              search_collection: this.collectionHandle || '',
            });
            refine();
          });
        });
      }
    }
  },

  renderQueryRules(options) {
    const { items } = options;
    const innerBanners = items?.filter((item) => item.type === 'banners');

    if (innerBanners?.length > 0) {
      this.smallCardBanners = innerBanners
        .reduce(
          (acc, item) =>
            acc.concat(
              item.list.filter((item) => !!item.imageUrl && !!item.position)
            ),
          []
        )
        .map((item) => ({
          ...item,
          type: 'banner',
        }));
    }
  },

  renderDynamicWidgets(options, isFirstRender) {
    const attributes = options.attributesToRender
      ?.map((attribute) => attribute.split('.').pop())
      .filter((attribute) => !['price', 'print'].includes(attribute));

    // this.facetsList = [...new Set(attributes)];
  },

  transformSizes(items) {
    return algoliaSortSizes(items);
  },

  transformColorSwatches(items) {
    const swatches = loadSwatches();
    const data = items.map((item) => ({
      ...item,
      swatch: swatches[item.value] || {},
    }));

    return data;
  },

  transformPrints(items) {
    const facets = items.map((item) => item.value);
    this.printsLoaded = false;

    getPrintFacets({ facets }).then(async (data) => {
      const ids = data
        .filter((item) => !!item.id)
        .map((item) => item.id)
        .map((id) => `gid://shopify/Collection/${id}`);
      await fetchCollections(ids);
      this.prints = data;
      this.printsLoaded = true;
    });

    return items;
  },

  transformPrintCollections(items) {
    const ids = items.map((item) => item.value);
    this.printsCollectionsLoaded = false;

    fetchCollections(ids)
      .then(() => {
        this.printsCollections = ids;
        this.printsCollectionsLoaded = true;
      })
      .catch((error) => {
        this.error = error;
      });

    return items;
  },

  transformAvailability(items) {
    const LABELS = {
      true: 'In stock',
      false: 'Out of stock',
    };
    const data = items.map((item) => ({
      ...item,
      label: LABELS[item.value] || item.label,
    }));

    return data;
  },

  onRenderSearch() {
    const { helper } = this.instantSearchClient;
    this.query = helper.state.query;
    this.loading = this.instantSearchClient.status === 'loading';
  },

  updateHeroSearchTitle(query, totalResults) {
    const title = document.querySelector('[data-hero-search-title]');
    if (title) {
      title.innerText = query
        ? `Showing ${totalResults} result${
            totalResults > 1 ? 's' : ''
          } for "${query}"`
        : 'Search Results';
    }
  },

  playCollectionVideo(media_id) {
    setTimeout(() => {
      const myVideo = document.querySelector(
        '.products-browser__media--' + media_id + ' video'
      );
      if (myVideo) {
        myVideo.play();
      }
    }, 2000);
  },

  scrollToLastPosition() {
    const raw = sessionStorage.getItem('algoliaScrollHandle');
    if (!raw) return;

    try {
      const data = JSON.parse(raw);

      const tryRestore = (payload) => {
        let attempts = 0;
        const maxAttempts = 10;
        const selector = `[data-product-handle="${payload.handle}"]`;

        const check = () => {
          attempts += 1;
          const activeElement = document.querySelector(selector);
          if (activeElement) {
            try {
              activeElement.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
              });
            } catch (e) {
              captureSentryMessage(
                'Error scrolling to last active element',
                'warning',
                {
                  ...payload,
                }
              );
              window.scrollTo({ top: payload.scrollY || 0, behavior: 'auto' });
            }
            sessionStorage.removeItem('algoliaScrollHandle');
            clearInterval(interval);
            return;
          }

          if (attempts >= maxAttempts) {
            // fallback: scroll to recorded Y if provided
            if (typeof payload.scrollY === 'number') {
              window.scrollTo({ top: payload.scrollY, behavior: 'auto' });
            }
            sessionStorage.removeItem('algoliaScrollHandle');
            clearInterval(interval);
          }
        };

        const interval = setInterval(check, 300);
        // run immediately once
        check();
      };

      tryRestore(data);
    } catch (e) {
      // If parsing fails, remove the storage key to avoid loops
      sessionStorage.removeItem('algoliaScrollHandle');
      captureSentryMessage('Error parsing algolia scroll handle', 'warning', {
        error: e.message,
      });
      window.scrollTo({ top: 0, behavior: 'auto' });
    }
  },

  handleBackToTopClick() {
    window.scrollTo({
      top: this.$root.offsetTop - document.querySelector('header').offsetHeight,
      behavior: 'smooth',
    });
  },

  handleProductCardClick(event) {
    const { handle } = event.detail;
    const payload = {
      handle,
      scrollY: window.scrollY || 0,
    };
    sessionStorage.setItem('algoliaScrollHandle', JSON.stringify(payload));

    if (this.query) {
      trackGoogleAnalyticsEvent('product_clicked_from_search', {
        search_term: this.query,
        product_handle: handle,
        active_filter_count: this.currentRefinementsCount,
        search_source: 'instantsearch',
        search_collection: this.collectionHandle || '',
      });
    }
  },

  handleClearAllRefinements() {
    this.refineClearAll && this.refineClearAll();
  },

  revealDescription() {
    this.descriptionRevealed = true;
  },

  collapseDescription() {
    this.descriptionRevealed = false;
  },

  trackViewSearchResults(results) {
    if (!results) return;

    // Algolia cannot accept more than 20 objectIDs per event
    // We chunk the hits array and send one event per chunk
    const MAX_OBJECTIDS_PER_EVENT = 20;
    const hits = results?.hits || [];

    for (let i = 0; i < hits.length; i += MAX_OBJECTIDS_PER_EVENT) {
      const chunk = hits.slice(i, i + MAX_OBJECTIDS_PER_EVENT);
      trackAlgoliaEvent('viewedObjectIDs', {
        eventName: 'Hits Viewed',
        index: window.ALGOLIA_INDEX_NAME,
        queryID: results?.queryID,
        objectIDs: chunk.map((hit) => hit.objectID),
        // positions: chunk.map((hit) => hit.position),
        algoliaSource: ['instantsearch'],
      });
    }

    trackGoogleAnalyticsEvent(
      results?.nbHits === 0 ? 'search_no_results' : 'view_search_results',
      {
        search_term: results?.query,
        active_filter_count: this.currentRefinementsCount,
        result_count: results?.nbHits,
        search_source: 'instantsearch',
        search_collection: this.collectionHandle || '',
        query_id: results?.queryID,
      }
    );
  },

  sendAnalyticsEvent(title, url, index) {
    trackGoogleAnalyticsEvent('card_banner_click', {
      collection_title: title,
      tile_index: index + 1,
      collection_url: url,
      event_action: 'click',
      search_source: 'instantsearch',
      search_collection: this.collectionHandle || '',
    });
  },
});

export default algoliaBrowser;
